// Pure render-mode logic — no React, no imports → unit-testable in isolation.
// Decides which renderer a chat uses (Clean HTML vs Terminal xterm) from the
// plugin's declared capabilities + the per-chat preference.
// See docs/spec/RENDER_MODES_SPEC.md.

export interface ViewCaps {
  interactive: boolean
  cleanView: boolean
  liveStream: boolean
  streamStrategy: 'structured' | 'plaintext' | 'screen-scrape'
  /** false = hide the live terminal zone (committed-only, e.g. grok). undefined/true = show. */
  liveView?: boolean
}

// Structural shapes (no cross-module imports) so this file compiles standalone.
// The real Chat / Plugin types are supersets and assign here by structural typing.
// First-class render modes. WhimerClean = two-zone, committed=JSONL chat-style;
// WhimerTerm = two-zone, committed=streamed terminal-style.
// See docs/reports/2026-06-04-whimerterm-two-zone-live-ir.md.
// 'native' = WhimerTerm² — same two-zone layout but committed/live come from the v2
// native plugin pipeline (native_commit / native_frame) instead of capture-pane.
// 'clean3' / 'term3' = WhimerClean³ / WhimerTerm³ — the v3 views (code-audit §5.1):
// committed = JSONL turns ONLY, live = native_frame ONLY. No native_commit / no oracle
// watermark → torn-table / missing-line bugs cannot occur by construction. clean3 = chat skin,
// term3 = terminal skin; both render the SAME WhimerV3View core (skin prop).
export type ViewName = 'clean' | 'whimerclean' | 'whimerterm' | 'terminal' | 'native' | 'clean3' | 'term3'

export interface ChatLike {
  agent_plugin: string | null
  connection_type: 'pty' | 'api' | 'cli'
  view_mode?: ViewName | null
  /** How the agent is spawned: 'native-pty' = direct PTY (no tmux pane). */
  spawn_mode?: 'tmux' | 'native-pty'
}
export interface PluginLike {
  name: string
  capabilities?: ViewCaps
}

// Fallbacks reproduce the legacy usesChatRenderer() defaults for when plugin
// capabilities haven't loaded from the API yet.
export const CLEAN_FALLBACK: ViewCaps = { interactive: false, cleanView: true,  liveStream: false, streamStrategy: 'structured' }
export const TERM_FALLBACK:  ViewCaps = { interactive: true,  cleanView: false, liveStream: false, streamStrategy: 'plaintext'  }
export const FULL_FALLBACK:  ViewCaps = { interactive: true,  cleanView: true,  liveStream: true,  streamStrategy: 'structured' }

/** Resolve the render capabilities for a chat (plugin-declared, else legacy fallback). */
export function capsFor(chat: ChatLike, plugins: PluginLike[]): ViewCaps {
  const p = plugins.find(pl => pl.name === chat.agent_plugin)
  if (p?.capabilities) return p.capabilities          // plugin declares its own caps → use them
  if (chat.connection_type === 'api') return CLEAN_FALLBACK
  if (chat.connection_type === 'cli') return TERM_FALLBACK
  if (!chat.agent_plugin) return TERM_FALLBACK         // no agent → terminal-only
  return FULL_FALLBACK
}

/** Which renderer to show, honoring per-chat preference within capability limits. */
export function effectiveView(chat: ChatLike, plugins: PluginLike[]): ViewName {
  const caps = capsFor(chat, plugins)
  // v3-only (2026-06-11): Clean³ (default) + Term³. Raw xterm terminal removed. A non-clean chat
  // (bash/zsh) or any terminal-flavored preference falls to Term³ (its live zone IS the native
  // terminal frame; committed=JSONL when the agent has one). All deprecated view_mode values
  // (clean/whimerclean/whimerterm/native/terminal) remap to a v3 heir — no DB migration needed.
  const v = chat.view_mode
  if (!caps.cleanView) return 'term3'
  if (v === 'term3' || v === 'terminal' || v === 'whimerterm' || v === 'native') return 'term3'
  return 'clean3'
}
